'use client';

import { useState, useRef, useEffect, memo } from 'react';
import styles from './SearchForm.module.scss';

interface SearchFormProps {
  placeholder?: string;
  onSearch?: (query: string) => void;
  onBlur?: () => void;
  autoFocus?: boolean;
  className?: string;
  'aria-label'?: string;
}

export const SearchForm = memo(({
  placeholder = '検索...',
  onSearch,
  onBlur,
  autoFocus = false,
  className = '',
  'aria-label': ariaLabel = '検索フォーム'
}: SearchFormProps) => {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (onSearch && query.trim()) {
      onSearch(query.trim());
    }
  };
  
  const handleClear = () => {
    setQuery('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };
  
  return (
    <form 
      onSubmit={handleSubmit} 
      onBlur={onBlur}
      className={`${styles.searchForm} ${className}`}
      role="search"
      aria-label={ariaLabel}
    >
      <div className={styles.inputWrapper}>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          className={styles.searchInput}
          aria-label="検索キーワードを入力"
        />
        {query && (
          <button
            type="button"
            onClick={handleClear}
            className={styles.clearButton}
            aria-label="検索をクリア"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path 
                d="M4 4L12 12M4 12L12 4" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
        <button 
          type="submit" 
          className={styles.searchButton}
          aria-label="検索実行"
          disabled={!query.trim()}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path 
              d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
            <path 
              d="M17 17l-4.35-4.35" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
    </form>
  );
});

export default SearchForm;